/**
 * Meeting Link Utilities
 * Handles encoding/decoding of room data for meeting links
 */

export interface MeetingLinkData {
  id: number;
  timestamp: number;
}

// URL-safe base64 helpers (no "+", "/", "=")
const toBase64Url = (input: string): string =>
  btoa(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');

const fromBase64Url = (input: string): string => {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((input.length + 3) % 4);
  return atob(padded);
};

/**
 * Encodes minimal room data (id + timestamp) into a URL-safe base64 string
 * @param roomData - Room data to encode (expects pk)
 * @returns Encoded string for URL
 */
export const encodeMeetingLink = (roomData: any): string => {
  try {
    if (!roomData.pk) {
      throw new Error('Missing required room id (pk)');
    }

    // Use short keys to reduce size
    const minimal = { i: roomData.pk, ts: Date.now() };
    const jsonString = JSON.stringify(minimal);
    return toBase64Url(jsonString);
  } catch (error) {
    throw new Error('Invalid room data for meeting link');
  }
};

/**
 * Decodes meeting link data from URL-safe base64 string
 * @param encodedData - Encoded string from URL
 * @returns Decoded minimal data
 */
export const decodeMeetingLink = (encodedData: string): MeetingLinkData => {
  try {
    const jsonString = fromBase64Url(encodedData);
    const decoded = JSON.parse(jsonString);

    const id = decoded.id ?? decoded.i;
    const timestamp = decoded.timestamp ?? decoded.ts;

    if (!id) {
      throw new Error('Invalid meeting link data: missing id');
    }

    return { id, timestamp } as MeetingLinkData;
  } catch (error) {
    throw new Error('Invalid meeting link format');
  }
};

/**
 * Generates a meeting link URL
 * @param roomData - Room data to encode (expects pk)
 * @param baseUrl - Base URL for the application
 * @returns Complete meeting link URL
 */
export const generateMeetingLink = (roomData: any, baseUrl: string): string => {
  const encodedData = encodeMeetingLink(roomData);
  return `${baseUrl}/call/${encodedData}`;
};

// Keeping the validator for potential future use, but not enforced now
export const isMeetingLinkValid = (linkData: MeetingLinkData, maxAgeHours: number = 24): boolean => {
  const now = Date.now();
  const maxAgeMs = maxAgeHours * 60 * 60 * 1000;
  return (now - linkData.timestamp) < maxAgeMs;
};